import { Form, Tooltip, Typography } from 'antd';
import BaseModal from 'components/BaseModal';
import _, { isUndefined, isEmpty, isNumber } from 'lodash'
import React, { useEffect, useState, forwardRef, useCallback, useMemo, useRef } from 'react';
import ActionMenuV2 from "components/ActionMenuV2";
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import ModalUtil from 'utils/modalUtil';
import { CellRenderers } from 'utils/tableUtils';
import Vocabulary from 'components/Vocabulary';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import SubProductEnvironmentTypeAPI from '../../../restAPI/SubProductEnvironmentTypeAPI';
import { PermissionSetMap } from 'constants/RBACConstants';
import message from "components/CustomMessage";
import useTriggerableEffect from 'react-triggerable-effect';
import { GITHUB_TICKET_TYPE_TEXT } from 'constants/RepositoryConstants';
import RepositoryAPI from 'restAPI/RepositoryAPI';
import TableContainerEvolved from 'components/TableContainerEvolved';
import EnvironmentName from 'views/GlobalSettings/EnvironmentName';
import { ExternalLinkIcon, InfoIcon } from 'components/Icons';
import { useHistory, useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min';
import { hasPermission } from 'utils/authService';
import { useHasDisabledEnvironment } from 'contexts/GlobalSettingsContext';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import CodeInsightsModal from './CodeInsightsModal';
import GlobalSettingAPI from 'restAPI/GlobalSettingAPI';

export default function RepositoryListing({ installation, title = "GitHub Repositories" }) {
    const [ciEnabled, setCiEnabled] = useState(null);
    const { url } = useRouteMatch();
    const history = useHistory();
    const tableRef = useRef();
    
    const installationId = installation?.id;

    useEffect(() => {
        const { promise, cancel } = GlobalSettingAPI.getCodeInsightsSettings();
        promise
          .then((res) => {
            setCiEnabled({
              codeInsights: !!res.codeInsights?.enabled,
              materialCodeChanges: !!res.materialCodeChanges?.enabled,
            });
          })
          .catch((e) => {
            console.error("Error fetching Code Insights settings:", e);
          });
        return cancel;
    }, []);

    const fetchRepoListing = useCallback((params) => {
        const queryParams = {
            ..._.omit(params,["filters"]),
            ...params.filters
        }
        if (!_.isEmpty(queryParams.product)) {
            queryParams.productId = queryParams.product
            delete queryParams.product
        }
        if (!_.isEmpty(queryParams.subproduct)) {
            queryParams.subProductId = queryParams.subproduct
            delete queryParams.subproduct
        }
        const { promise, cancel } = ToolsIntegrationsAPI.getRepoDetails(installationId, queryParams);
        return {promise, cancel}
    }, [installationId])

    const fetchRepoFilter = useCallback((params) => {
        return ToolsIntegrationsAPI.getRepoFilters(installationId, params);
    }, [installationId])

    const getTableRef = useCallback((ref) => {
        tableRef.current = ref;
    }, []);

    const showCodeInsightsModal = useCallback((repository) => {
        ModalUtil.show({
            content: <CodeInsightsModal
                repository={repository}
                installation={installation}
            />,
            onClose: () => {
                tableRef.current?.refresh();
            }
        });
    }, [installation]);

    const editRepositoryForm = (repository) => {
        const data = {
            ...repository,
            url: repository.repoUrl,
            defaultBranch: repository.branchName,
            subProductDetails: repository.subProductId ? {
                repoId: repository.id,
                productId: repository.productId,
                subProductId: repository.subProductId,
                environmentId: repository.environmentId,
                branch: repository.branchName,
                enableTicket: repository.enableTicket,
                ticketConfigurationId: repository.ticketConfigurationId,
                enableCodeScan: repository.enableCodeScan,
                enableDependabot: repository.enableDependabot,
                enableSecretScan: repository.enableSecretScan,
            } : undefined,
        }
        ModalUtil.show({
            content: <EditRepositoryForm repository={data} installation={installation} history={history} url={url}/>,
            afterClose:  () => {
                tableRef.current.refresh();
                tableRef.current.refreshFilters();
            }
        })
    }

    const deleteRepoMapping = async (id) => {
        try{
            await ToolsIntegrationsAPI.deleteRepositoryMapping(id).promise;
            message.success("Deleted successfully");
            tableRef.current.refresh();
        } catch (err) {
            console.log(err);
            throw err;
        }
    }

    const productVocab = <Vocabulary name="product" />;
    const subProductVocab = <Vocabulary name="subProduct" />;

    const items = (record) => {
        const codeInsightsGloballyDisabled = !ciEnabled?.codeInsights || !ciEnabled?.materialCodeChanges;
        let codeInsightsTooltip = "";
        if (!ciEnabled) {
            codeInsightsTooltip = "Error: Could not load Code Insights settings.";
        } else if (!record.subProductName) {
            codeInsightsTooltip = "Repository must be mapped to enable Code Insights";
        } else if (codeInsightsGloballyDisabled) {
            if (!ciEnabled.codeInsights) {
                codeInsightsTooltip = "Code Insights is disabled in Global Settings";
            } else {
                codeInsightsTooltip = "Material Code Changes is disabled in Global Settings";
            }
        }
        return [{
        type: "edit",
        text: !record.subProductName && "Assign",
        tooltip: "Edit Repository details",
        disabled: !(hasPermission(PermissionSetMap.WRITE_TOOLS) || hasPermission(PermissionSetMap.EDIT_TOOL_CONFIG)),
    },
    {
        type: "codeInsights",
        text: "Code Insights",
        icon: <InfoIcon/>,
        tooltip: codeInsightsTooltip,
        disabled: !record.subProductName || codeInsightsGloballyDisabled,
    },
    {
        type: "delete",
        permission: PermissionSetMap.DELETE_TOOL,
        tooltip: record.subProductName ? <>Delete {productVocab}/{subProductVocab} mapping</> : "",
        disabled: !record.subProductName,
        promptMessage: <>This will delete repository {productVocab}/{subProductVocab} mapping.</>,
        text:"Delete Mapping"
    }]
   }

    const onClick = async (_, record) => {
        const {data} = record
        switch(record.name){
            case "edit": 
                editRepositoryForm(data)
                break;
            case "delete":
                await deleteRepoMapping(data.id)
                break;
            case "codeInsights":
                showCodeInsightsModal(data);
                break;
            default:
                break;
        }
    }

    const columns = [{
        title: "Name",
        dataIndex: "name",
        width: 150,
        showFilter: true,
        onlySearch: true
    }, {
        title: "Created On",
        dataIndex: "repoCreatedAt",
        key: "repoCreatedAt",
        showSort: true,
        render: CellRenderers.TIME_RELATIVE,
        width: 100
    }, {
        title: "Updated On",
        dataIndex: "repoUpdatedAt",
        key: "repoUpdatedAt",
        width: 100,
        showSort: true,
        render: CellRenderers.TIME_RELATIVE
    },{
        title: productVocab,
        dataIndex: "product",
        width: 100,
        showFilter: true,
        render:(product,record) => CellRenderers.VALUE_OR_NA(record.productName),
    },{
        title: subProductVocab,
        dataIndex: "subproduct",
        width: 100,
        showFilter: true,
        render:(subproduct,record) => CellRenderers.VALUE_OR_NA(record.subProductName),
    },
    {
        title: "Branch",
        dataIndex: "branchName",
        width: 100
    },
    {
        title: "Action",
        width: 60,
        render: (record) =>  <ActionMenuV2 items={items(record)} onClick={onClick} data={record} />,
        className: "text-center",
    }
];

    return (
        <TableContainerEvolved
            columns={columns}
            tableRef={getTableRef}
            api={fetchRepoListing}
            filtersApi={fetchRepoFilter}
            title={title}
            enableScroll
            disableURLUpdate={true}
        />
    )
}

export const EditRepositoryForm = forwardRef(function EditRepositoryForm({repository, installation, ...props}, ref) {
    const subProductDetails = repository?.subProductDetails;

    const [form] = Form.useForm();
    const [selectedProduct, setSelectedProduct] = useState(subProductDetails?.productId);
    const [selectedSubProduct, setSelectedSubProduct] = useState(subProductDetails?.subProductId);
    const [environmentList, setEnvironmentList] = useState([]);
    const [disabledTicketing, setDisabledTicketing] = useState(false);
    const [loading, setLoading] = useState(false);
    const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();
    const featureFlags = useFeatureFlag()
    const setTicketingField = useCallback(
        res => {
            let isDisabled = false;
            if (!isEmpty(res)) {
                if ((!subProductDetails?.ticketConfigurationId && res.ticketSystemType === GITHUB_TICKET_TYPE_TEXT) || (res.ticketConfigurationId && res.ticketSystemType !== GITHUB_TICKET_TYPE_TEXT )) {
                    isDisabled = true;
                }
            }
            setDisabledTicketing(isDisabled);
        },
        [subProductDetails],
    )

    const getJiraIntegrationDetails = useCallback(
        (product, subProduct) => {
            if (isNumber(product) && isNumber(subProduct)) {
                setLoading(true);
                const { promise, cancel} = ToolsIntegrationsAPI.getJiraIntegrationByProductSubProduct({product, subProduct})
                promise.then(res => {
                    setTicketingField(res);
                    setLoading(false);
                }).catch(e => setLoading(false));
                return cancel;
            }
        },
    [setTicketingField])

    useEffect(() => {
        let cancel = _.noop;
        setLoading(true);
        if (!isEmpty(subProductDetails)) {
            if (!subProductDetails.ticketConfigurationId) {
             cancel = getJiraIntegrationDetails(subProductDetails.productId, subProductDetails.subProductId);
            }
        }
        setLoading(false);
        return cancel;
    }, [subProductDetails, getJiraIntegrationDetails])
        
    const onSave = useCallback(async () => {
        await form.validateFields();
        const values = form.getFieldsValue();
        if(hasDisabledEnvironment){
            values.environmentId = environmentList.find(env => env.name === envValue)?.id;
        }
        _.assign(values, {name: repository.name, repoType: "GITHUB", repoUrl: repository.url, token: repository.token || installation?.token, githubInstallationId: installation?.installationId, gitInstallationId: installation?.id });
        let successMessage = "Details Saved";
        try{
            if(subProductDetails?.repoId){
                await ToolsIntegrationsAPI.putGithubEditRepositoryDetails(subProductDetails.repoId, values).promise
            }
            else {
                const response = await ToolsIntegrationsAPI.postGithubEditRepositoryDetails(values).promise;
                if(response.enableCodeScan || response.enableDependabot || response.enableSecretScan){
                    successMessage = <>Auto Create configuration executed for GitHub Repository <Typography.Link onClick={()=>props.history.push(`${props.url}/github/listing/repositories`)}> <ExternalLinkIcon className='font-size-md' style={{color: "#fff"}}/></Typography.Link></>;
                }
            }
            message.success(successMessage);
        } catch(err) {
            console.log(err);
            throw err;
        }
    }, [form, repository.name, repository.url, repository.token, installation, subProductDetails, props.history, props.url, hasDisabledEnvironment, envValue, environmentList]);

    //Fix for ENG-6519 && ENG-6659
    const repositoryName = repository?.data?.name || repository?.name || ''

    const baseModalProps = {
        title: `Edit ${repositoryName} repository details`,
        onOk: onSave,
        disabled: loading || !hasPermission(PermissionSetMap.EDIT_TOOL_CONFIG)
    }

    const triggerJiraIntegrationAPI = useTriggerableEffect(() =>  getJiraIntegrationDetails(form.getFieldValue('productId'), form.getFieldValue('subProductId')), [form, selectedProduct, selectedSubProduct, getJiraIntegrationDetails]);

    const onChange = useCallback((values) => {
        if(values.productId){
            form.setFieldsValue({subProductId: null});
            setSelectedProduct(values.productId);
            setSelectedSubProduct(); // setting undefined subProduct
        }
        if(values.subProductId){
            form.setFieldsValue({environmentId: null});
            setSelectedSubProduct(values.subProductId);
            
        }
        (!isUndefined(values.productId) || !isUndefined(values.subProductId)) && triggerJiraIntegrationAPI();
    }, [form, triggerJiraIntegrationAPI]);

    useEffect(() => {
        if(selectedProduct && form){
            setEnvironmentList([]);
        }
    }, [form, selectedProduct])

    useEffect(() => {
        if(selectedSubProduct && form){
            setEnvironmentList(null);
            const { promise, cancel } = SubProductEnvironmentTypeAPI.getAllQuick(selectedSubProduct)
            promise.then(setEnvironmentList, _.noop);
            return cancel;
        }
    },[selectedSubProduct, form])

    const productVocab = <Vocabulary name="product" />;
    const subProductVocab = <Vocabulary name="subProduct" />;

    const installationID = installation?.id || repository.gitInstallation?.id;
    const branchAPIParams = useMemo(() => ({
        type: 'GITHUB',
        repository: repository.name,
        configId: installationID
    }), [repository.name, installationID]);

    const getBranches = useCallback((params) => {
        const {promise, cancel} = RepositoryAPI.getBranches({...params, ...branchAPIParams}, true);
        return {promise, cancel}
    }, [branchAPIParams])

    const fields = [{
        label: productVocab,
        name: "productId",
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        rules: [{
            required: true,
            message: <>{productVocab} is required</>
        }],
        props: {
            dropdownType: 'product',
            showAddButton: true,
            onCreate: (createdProduct) => {
                form.setFieldsValue({
                    productId: createdProduct.id,
                    subProductId: null,
                    environmentId: null
                });
                setSelectedProduct(createdProduct.id);
                setSelectedSubProduct();
            },
            autoFocus: true
        }
    }, {
        label: subProductVocab,
        name: "subProductId",
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        rules: [{
            required: true,
            message: <>{subProductVocab} is required</>
        }],
        props: {
            productId: selectedProduct,
            showAddButton: !!selectedProduct,
            dropdownType: 'subProduct',
            onChange: (e) => form.setFieldsValue({environmentId: null}),
            onCreate: (createdSubproduct) => {
                form.setFieldsValue({
                    subProductId: createdSubproduct.id,
                    environmentId: null
                });
                setSelectedSubProduct(createdSubproduct.id);
            }
        }
    }, ...(hasDisabledEnvironment ? [] : [{
        label: "Environment",
        name: "environmentId",
        type: FieldTypes.SELECT,
        options: environmentList?.map((env) => ({
            label: <EnvironmentName name={env.name} />,
            value: env.id,
        })),
        loading: _.isNull(environmentList),
        rules: [{
            required: true,
            message: "Environment is required"
        }]
    }]),
    {
        label: "Branch",
        name: "branch",
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        required: true,
        notFoundContent: "No branches defined for this repository",
        props: {
            api: getBranches,
            showDefaultError: false
        }
    },
    ...(!featureFlags.GITHUB_ISSUES_CONFIG
        ?
        [{
            name: "enableTicket",
            label: <><Tooltip title={disabledTicketing ? <><Vocabulary name="product" isPlural={false} /> and <Vocabulary name="subProduct" isPlural={false} /> are already configured to other ticket.</> : ''}>
                    Enable GitHub Issues <Tooltip title={"Create GitHub issues for findings associated with the repository."}> <InfoIcon/></Tooltip>
                </Tooltip></>,
            type: FieldTypes.SWITCH,
            labelAlign: "left",
            inline: true,
            disabled: disabledTicketing
        }]
        :
        []
    )];

    const initialValues = {
        productId: selectedProduct,
        subProductId: subProductDetails?.subProductId,
        environmentId: subProductDetails?.environmentId,
        branch: subProductDetails?.branch || repository?.defaultBranch,
        enableTicket: subProductDetails?.enableTicket,
        enableDependabot: true,
        enableCodeScan: true,
    };

    return (
        <BaseModal
            modalProps={baseModalProps}
            ref={ref}
            afterClose={props.afterClose}
            loading={loading}
        >
            <Form 
                form={form}
                layout="vertical"
                onValuesChange={onChange}
                initialValues={initialValues}
            >
                {generateFormFields(fields)}
            </Form>
        </BaseModal>
    )
})
